/**
 * Room / ceiling boundary detection from surrounding wall geometry.
 *
 * BIM's "Room" and "Ceiling > Automatic Ceiling" pick the closed boundary
 * formed by the enclosing wall faces. Here the room at a click point is the
 * convex polygon obtained by clipping a large plate with the inner face
 * half-plane of every wall line close to the point — exact for the rectilinear
 * rooms this app draws, graceful for rotated walls.
 */
import * as THREE from 'three';
import { BIMElement } from '../core/types';

export interface WallLine2D {
    /** A point on the wall centerline (world XY). */
    px: number;
    py: number;
    /** Unit direction along the wall. */
    dx: number;
    dy: number;
    /** Wall thickness (m). */
    thickness: number;
    /** Base elevation of the wall (m). */
    baseZ: number;
    height: number;
    id: string;
}

type Pt = [number, number];

/** Collect usable straight wall segments from a project element map. */
export function collectWallLines(elements: Map<string, BIMElement>): WallLine2D[] {
    const out: WallLine2D[] = [];
    elements.forEach((el) => {
        if (el.category !== 'Walls') return;
        const g = (el as any).geometryData;
        if (!g || !g.start || !g.end) return;
        const a = new THREE.Vector2(g.start[0], g.start[1]);
        const b = new THREE.Vector2(g.end[0], g.end[1]);
        const dir = b.clone().sub(a);
        if (dir.length() < 0.05) return;
        dir.normalize();
        out.push({
            px: a.x, py: a.y,
            dx: dir.x, dy: dir.y,
            thickness: g.thickness ?? 0.2,
            baseZ: g.baseOffset ?? 0,
            height: g.height ?? 3,
            id: el.id,
        });
    });
    return out;
}

function pointLineDistance(w: WallLine2D, x: number, y: number): number {
    // Distance from point to the infinite centerline of the wall.
    const vx = x - w.px;
    const vy = y - w.py;
    const cross = w.dx * vy - w.dy * vx;
    return Math.abs(cross);
}

/**
 * Compute the closed boundary polygon (XY) of the room containing `point`,
 * using the inner faces of nearby walls. Returns null when no wall encloses
 * the point from enough directions (open space).
 */
export function computeRoomBoundary(
    point: THREE.Vector3,
    walls: WallLine2D[],
    maxWallDistance = 25
): { boundary: Pt[]; baseZ: number } | null {
    // Keep only walls whose centerline is near enough to plausibly bound this room.
    const near = walls.filter((w) => pointLineDistance(w, point.x, point.y) < maxWallDistance);
    if (near.length < 3) return null;

    // Start from a generously large rectangle around the point and clip it
    // against the inner half-plane of every wall (Sutherland–Hodgman).
    const R = maxWallDistance;
    let poly: Pt[] = [
        [point.x - R, point.y - R],
        [point.x + R, point.y - R],
        [point.x + R, point.y + R],
        [point.x - R, point.y + R],
    ];

    let baseZ = 0;
    let baseZFound = false;

    for (const w of near) {
        // Signed distance of the room point to the wall centerline; the room
        // boundary face is the centerline shifted toward the point by half the
        // wall thickness.
        const vx = point.x - w.px;
        const vy = point.y - w.py;
        const cross = w.dx * vy - w.dy * vx;
        const sgn = cross >= 0 ? 1 : -1;
        const faceDist = Math.max(Math.abs(cross) - w.thickness / 2, 0);

        // Boundary line: point on centerline closest to room point, pushed to
        // the face. Inside test: distance from the (inward) normal axis <= faceDist.
        const npx = -w.dy * sgn; // inward unit normal (toward the room)
        const npy = w.dx * sgn;
        // Origin of the face line: centerline point + normal * faceDist
        const ox = w.px + npx * faceDist;
        const oy = w.py + npy * faceDist;
        // A point q is inside if dot(q - o, -normal) >= 0 (toward centerline).
        const clip = (q: Pt): number => -((q[0] - ox) * npx + (q[1] - oy) * npy);

        const input = poly;
        const output: Pt[] = [];
        let clippedAnything = false;
        for (let i = 0; i < input.length; i++) {
            const cur = input[i];
            const nxt = input[(i + 1) % input.length];
            const dc = clip(cur);
            const dn = clip(nxt);
            const inCur = dc <= 0;
            const inNxt = dn <= 0;
            if (inCur) output.push(cur);
            if (inCur !== inNxt) {
                const t = dc / (dc - dn);
                output.push([cur[0] + t * (nxt[0] - cur[0]), cur[1] + t * (nxt[1] - cur[1])]);
                clippedAnything = true;
            }
        }
        if (clippedAnything && !baseZFound) {
            baseZ = w.baseZ;
            baseZFound = true;
        }
        if (output.length < 3) return null;
        poly = output;
    }

    // Reject the un-clipped fallback plate: at least a few clips must have bound
    // the point region, and the polygon must be meaningfully smaller than R².
    const area = polygonArea(poly);
    if (!Number.isFinite(area) || area < 0.5 || area > Math.PI * R * R) return null;

    return { boundary: poly, baseZ };
}

export function polygonArea(points: Pt[]): number {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += points[i][0] * points[j][1];
        area -= points[j][0] * points[i][1];
    }
    return Math.abs(area) / 2;
}
